import React, { useState } from 'react';
import { apiFetch } from './lib/api';

// ==================== TYPES ====================
type ContractType = 'FULL_TIME' | 'PART_TIME' | 'CONTRACTOR';
type EmployeeSector = 'PRIVATE' | 'GOVERNMENTAL' | 'UNKNOWN';
type IncomeDataSource = 'MASDAR' | 'DAKHLI' | 'OPEN_BANKING';
type IncomeComponentType = 'BASE' | 'ADDITIONAL' | 'COMMERCIAL';

type AdditionalIncomeType = 'PRIVATE_BUSINESS' | 'INCENTIVES' | 'RENTAL' | 'SECOND_JOB' | 'PENSION';

type CommercialSourceType = 'SALARIED' | 'SELF_EMPLOYED' | 'REAL_ESTATE_YIELD';

interface AdditionalIncomeEntry {
	type: AdditionalIncomeType | string;
	value: number;
	source: IncomeDataSource;
	fetchedAt: string;
}

interface CommercialIncomeEntry {
	source: CommercialSourceType | string;
	amount: number;
	dataSource: IncomeDataSource;
	fetchedAt: string;
}

interface IncomeBreakdown {
	hasBaseIncome: boolean;
	baseIncome: number;
	additional: AdditionalIncomeEntry[];
	commercial: CommercialIncomeEntry[];
	components: IncomeComponentType[];
}

interface EmploymentData {
	contractType: ContractType;
	sector: EmployeeSector;
	jobTitle: string;
	netIncome: number;
	lengthOfServiceInMonths: number;
	lengthOfServiceOfPreviousEmployerInMonths?: number;
	isSalaried?: boolean;
	dataSource?: IncomeDataSource;
	incomeBreakdown?: IncomeBreakdown;
	fetchedAt: string;
}

interface ApiError {
	statusCode: number;
	message: string;
}

interface EmploymentApiResponse {
	success: boolean;
	data?: EmploymentData;
	error?: ApiError;
}

// ==================== UTILITY FUNCTIONS ====================
const formatCurrency = (amount: number): string => {
	if (amount === undefined || amount === null) return 'SAR 0';
	return new Intl.NumberFormat('en-SA', {
		style: 'currency',
		currency: 'SAR',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatDate = (iso: string): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (isNaN(date.getTime())) return iso;
	return date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});
};

const formatDateTime = (iso: string): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (isNaN(date.getTime())) return iso;
	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const formatDuration = (months: number): string => {
	if (months === undefined || months === null) return '—';
	const years = Math.floor(months / 12);
	const remMonths = months % 12;
	const parts: string[] = [];
	if (years > 0) parts.push(`${years} yr${years !== 1 ? 's' : ''}`);
	if (remMonths > 0) parts.push(`${remMonths} mo${remMonths !== 1 ? 's' : ''}`);
	const human = parts.length ? parts.join(' ') : '0 mos';
	return `${months} month${months !== 1 ? 's' : ''} (${human})`;
};

const humanizeLabel = (value: string): string =>
	value
		.split('_')
		.map((w) => w.charAt(0) + w.slice(1).toLowerCase())
		.join(' ');

// ==================== SUB-COMPONENTS ====================
const Chip: React.FC<{ children: React.ReactNode; color?: string }> = ({
	children,
	color = 'bg-gray-100 text-gray-700',
}) => (
	<span className={`inline-flex items-center font-semibold rounded-full px-2.5 py-0.5 text-xs ${color}`}>
		{children}
	</span>
);

const contractTypeColor = (type: ContractType): string => {
	switch (type) {
		case 'FULL_TIME':
			return 'bg-green-100 text-green-800';
		case 'PART_TIME':
			return 'bg-yellow-100 text-yellow-800';
		case 'CONTRACTOR':
			return 'bg-purple-100 text-purple-800';
		default:
			return 'bg-gray-100 text-gray-700';
	}
};

const sectorColor = (sector: EmployeeSector): string => {
	switch (sector) {
		case 'PRIVATE':
			return 'bg-blue-100 text-blue-800';
		case 'GOVERNMENTAL':
			return 'bg-indigo-100 text-indigo-800';
		default:
			return 'bg-gray-100 text-gray-600';
	}
};

const dataSourceColor = (source: string): string => {
	switch (source) {
		case 'MASDAR':
			return 'bg-teal-100 text-teal-800';
		case 'DAKHLI':
			return 'bg-cyan-100 text-cyan-800';
		case 'OPEN_BANKING':
			return 'bg-orange-100 text-orange-800';
		default:
			return 'bg-gray-100 text-gray-600';
	}
};

const componentColor = (component: IncomeComponentType): string => {
	switch (component) {
		case 'BASE':
			return 'bg-blue-100 text-blue-800';
		case 'ADDITIONAL':
			return 'bg-amber-100 text-amber-800';
		case 'COMMERCIAL':
			return 'bg-rose-100 text-rose-800';
		default:
			return 'bg-gray-100 text-gray-600';
	}
};

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
		<span className="text-sm text-gray-500">{label}</span>
		<span className="text-sm font-medium text-gray-900 text-right">{children}</span>
	</div>
);

// ---- Employment Information ----
const EmploymentInfoCard: React.FC<{ data: EmploymentData }> = ({ data }) => (
	<div className="bg-white p-6 rounded-lg shadow-sm border">
		<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Employment Information</h3>
		<div className="space-y-1">
			<InfoRow label="Job Title">{data.jobTitle}</InfoRow>
			<InfoRow label="Contract Type">
				<Chip color={contractTypeColor(data.contractType)}>{humanizeLabel(data.contractType)}</Chip>
			</InfoRow>
			<InfoRow label="Sector">
				<Chip color={sectorColor(data.sector)}>{humanizeLabel(data.sector)}</Chip>
			</InfoRow>
			<InfoRow label="Service Duration">{formatDuration(data.lengthOfServiceInMonths)}</InfoRow>
			{data.lengthOfServiceOfPreviousEmployerInMonths !== undefined && (
				<InfoRow label="Previous Employer Service">
					{formatDuration(data.lengthOfServiceOfPreviousEmployerInMonths)}
				</InfoRow>
			)}
		</div>
	</div>
);

// ---- Income Summary ----
const IncomeSummaryCard: React.FC<{ data: EmploymentData }> = ({ data }) => (
	<div className="bg-white p-6 rounded-lg shadow-sm border">
		<h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Income Summary</h3>

		<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center mb-4">
			<p className="text-xs text-blue-600 uppercase tracking-wide font-medium">Total Net Income</p>
			<p className="text-3xl font-bold text-blue-700 mt-1">{formatCurrency(data.netIncome)}</p>
			<p className="text-xs text-blue-500 mt-1">Consolidated monthly (single source of truth)</p>
		</div>

		<div className="space-y-1">
			{data.dataSource !== undefined && (
				<InfoRow label="Data Source">
					<Chip color={dataSourceColor(data.dataSource)}>{humanizeLabel(data.dataSource)}</Chip>
				</InfoRow>
			)}
			{data.isSalaried !== undefined && (
				<InfoRow label="Is Salaried">
					<Chip color={data.isSalaried ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
						{data.isSalaried ? 'Yes' : 'No'}
					</Chip>
				</InfoRow>
			)}
		</div>
	</div>
);

// ---- Income Breakdown ----
const IncomeBreakdownCard: React.FC<{ breakdown: IncomeBreakdown }> = ({ breakdown }) => {
	const showAdditional = breakdown.additional && breakdown.additional.length > 0;
	const showCommercial = breakdown.commercial && breakdown.commercial.length > 0;

	return (
		<div className="bg-white p-6 rounded-lg shadow-sm border">
			<div className="flex items-center justify-between mb-4 border-b pb-2">
				<h3 className="text-lg font-semibold text-gray-800">Income Breakdown</h3>
				{breakdown.components && breakdown.components.length > 0 && (
					<div className="flex items-center gap-1.5">
						{breakdown.components.map((c) => (
							<Chip key={c} color={componentColor(c)}>
								{c}
							</Chip>
						))}
					</div>
				)}
			</div>

			{/* Base Income */}
			{breakdown.hasBaseIncome && (
				<div className="bg-gray-50 border rounded-lg p-4 flex items-center justify-between mb-4">
					<span className="text-sm font-medium text-gray-700">Base Income</span>
					<span className="text-lg font-bold text-gray-900">{formatCurrency(breakdown.baseIncome)}</span>
				</div>
			)}

			{/* Additional Income */}
			{showAdditional && (
				<div className="mb-4">
					<h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
						Additional Income
						<Chip color="bg-amber-100 text-amber-800">{breakdown.additional.length}</Chip>
					</h4>
					<div className="space-y-2">
						{breakdown.additional.map((entry, i) => (
							<div
								key={`${entry.type}-${i}`}
								className="border rounded-lg p-3 bg-gray-50 flex items-center justify-between gap-3">
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-sm font-medium text-gray-800">{humanizeLabel(entry.type)}</span>
									<Chip color={dataSourceColor(entry.source)}>{humanizeLabel(entry.source)}</Chip>
									<span className="text-xs text-gray-400">{formatDate(entry.fetchedAt)}</span>
								</div>
								<span className="text-sm font-bold text-gray-900 whitespace-nowrap">
									{formatCurrency(entry.value)}
								</span>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Commercial Income */}
			{showCommercial && (
				<div>
					<h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
						Commercial Income
						<Chip color="bg-rose-100 text-rose-800">{breakdown.commercial.length}</Chip>
					</h4>
					<div className="space-y-2">
						{breakdown.commercial.map((entry, i) => (
							<div
								key={`${entry.source}-${i}`}
								className="border rounded-lg p-3 bg-gray-50 flex items-center justify-between gap-3">
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-sm font-medium text-gray-800">{humanizeLabel(entry.source)}</span>
									<Chip color={dataSourceColor(entry.dataSource)}>{humanizeLabel(entry.dataSource)}</Chip>
									<span className="text-xs text-gray-400">{formatDate(entry.fetchedAt)}</span>
								</div>
								<span className="text-sm font-bold text-gray-900 whitespace-nowrap">
									{formatCurrency(entry.amount)}
								</span>
							</div>
						))}
					</div>
				</div>
			)}

			{!breakdown.hasBaseIncome && !showAdditional && !showCommercial && (
				<p className="text-sm text-gray-500 italic">No income components to display.</p>
			)}
		</div>
	);
};

// ---- Results ----
const EmploymentResults: React.FC<{ data: EmploymentData }> = ({ data }) => {
	const [showRawJson, setShowRawJson] = useState(false);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2 text-sm text-gray-500">
				<span>🕒</span>
				<span>Data as of: {formatDateTime(data.fetchedAt)}</span>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<EmploymentInfoCard data={data} />
				<IncomeSummaryCard data={data} />
			</div>

			{data.incomeBreakdown && <IncomeBreakdownCard breakdown={data.incomeBreakdown} />}

			{/* Raw JSON Viewer */}
			<div className="bg-white rounded-lg border shadow-sm">
				<button
					onClick={() => setShowRawJson(!showRawJson)}
					className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
					<span>Raw JSON Response</span>
					<span className="text-gray-400">{showRawJson ? '▲' : '▼'}</span>
				</button>
				{showRawJson && (
					<div className="px-4 pb-4">
						<pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto max-h-96 font-mono">
							{JSON.stringify({ success: true, data }, null, 2)}
						</pre>
					</div>
				)}
			</div>
		</div>
	);
};

// ---- Empty / Error States ----
const NotFoundState: React.FC<{ nationalId: string }> = ({ nationalId }) => (
	<div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-10 text-center">
		<div className="text-4xl mb-3">📭</div>
		<h3 className="text-lg font-semibold text-gray-700">No Employment Data Found</h3>
		<p className="text-sm text-gray-500 mt-1">
			No employment record exists yet for National ID{' '}
			<span className="font-mono text-gray-700">{nationalId}</span>.
		</p>
		<p className="text-xs text-gray-400 mt-2">This is a normal state when data has not been fetched yet.</p>
	</div>
);

const ErrorDisplay: React.FC<{ message: string; statusCode?: number }> = ({ message, statusCode }) => (
	<div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
		<div className="flex items-start gap-3">
			<span className="text-red-500 text-2xl">&#9888;</span>
			<div className="flex-1">
				<h3 className="text-lg font-semibold text-red-800">
					Error
					{statusCode && <span className="ml-2 text-sm font-normal text-red-600">({statusCode})</span>}
				</h3>
				<p className="text-sm text-red-700 mt-1">{message}</p>
			</div>
		</div>
	</div>
);

// ==================== MAIN COMPONENT ====================
interface EmploymentIncomeSectionProps {
	baseUrl: string;
	nationalId?: string;
}

const EmploymentIncomeSection: React.FC<EmploymentIncomeSectionProps> = ({
	baseUrl,
	nationalId: initialNationalId,
}) => {
	const [nationalId, setNationalId] = useState(initialNationalId || '');
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState<EmploymentData | null>(null);
	const [httpStatus, setHttpStatus] = useState<number | null>(null);
	const [notFound, setNotFound] = useState(false);
	const [error, setError] = useState('');
	const [hasFetched, setHasFetched] = useState(false);

	const isValidNationalId = (id: string): boolean => /^[12]\d{9}$/.test(id);

	const handleSubmit = async () => {
		const trimmed = nationalId.trim();
		if (!trimmed) {
			setError('National ID is required');
			return;
		}
		if (!isValidNationalId(trimmed)) {
			setError('National ID must be 10 digits and start with 1 or 2');
			return;
		}

		setIsLoading(true);
		setData(null);
		setHttpStatus(null);
		setNotFound(false);
		setError('');

		try {
			const res = await apiFetch(`${baseUrl}/api/v1/employment/${trimmed}`);
			setHttpStatus(res.status);

			if (res.status === 404) {
				setNotFound(true);
				return;
			}

			const result: EmploymentApiResponse = await res.json();

			if (res.ok && result.success && result.data) {
				setData(result.data);
			} else {
				setError(result.error?.message || `Request failed with status ${res.status}`);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'An unexpected error occurred while calling the API');
		} finally {
			setIsLoading(false);
			setHasFetched(true);
		}
	};

	const handleReset = () => {
		setNationalId(initialNationalId || '');
		setData(null);
		setHttpStatus(null);
		setNotFound(false);
		setError('');
		setHasFetched(false);
	};

	return (
		<div className="space-y-6">
			{/* Request Form */}
			<div className="bg-white p-6 rounded-lg shadow-sm border">
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-lg font-semibold text-gray-800">Employment & Income Details</h3>
					<span className="text-xs font-mono bg-blue-100 text-blue-700 px-2 py-1 rounded">
						GET /employment/:nationalId
					</span>
				</div>

				<p className="text-sm text-gray-600 mb-6">
					Retrieve full employment data plus a complete income breakdown for a given National ID.
				</p>

				<div className="space-y-4">
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							National ID <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							value={nationalId}
							onChange={(e) => setNationalId(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
							placeholder="10 digits (e.g., 1234567890)"
							disabled={isLoading}
							className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm font-mono"
						/>
					</div>

					<div className="flex items-center gap-3 pt-2">
						<button
							onClick={handleSubmit}
							disabled={isLoading || !nationalId.trim()}
							className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2">
							{isLoading ? (
								<>
									<svg
										className="animate-spin h-4 w-4"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24">
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
										/>
									</svg>
									Fetching...
								</>
							) : hasFetched ? (
								'Refresh'
							) : (
								'Fetch Details'
							)}
						</button>
						<button
							onClick={handleReset}
							disabled={isLoading}
							className="px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors text-sm">
							Reset
						</button>
					</div>
				</div>
			</div>

			{/* Validation / API Error */}
			{error && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-sm text-red-700">{error}</p>
				</div>
			)}

			{/* HTTP Status + Results */}
			{httpStatus !== null && (
				<div className="space-y-4">
					<div className="flex items-center gap-2">
						<span
							className={`text-xs font-mono px-2 py-1 rounded ${
								httpStatus === 200
									? 'bg-green-100 text-green-700'
									: httpStatus === 404
									? 'bg-yellow-100 text-yellow-700'
									: 'bg-red-100 text-red-700'
							}`}>
							HTTP {httpStatus}
						</span>
						<span className="text-xs text-gray-400">|</span>
						<span className="text-xs text-gray-500">Response received</span>
					</div>

					{notFound ? (
						<NotFoundState nationalId={nationalId.trim()} />
					) : data ? (
						<EmploymentResults data={data} />
					) : (
						!error && <ErrorDisplay message="Unknown error" statusCode={httpStatus || undefined} />
					)}
				</div>
			)}
		</div>
	);
};

export default EmploymentIncomeSection;
